import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  FormHelperText,
  Autocomplete,
  Chip
} from '@mui/material';

/**
 * CompanyInformationStep Component - User Story 2
 * First step in the onboarding wizard for collecting company details
 * 
 * Following VegaDigital Constitution: WCAG 2.1 AA compliance, comprehensive validation
 */

interface CompanyInfoData {
  company_name?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  company_description?: string;
  phone?: string;
  address?: string;
  country?: string;
}

interface CompanyInformationStepProps {
  data: CompanyInfoData;
  onChange: (data: CompanyInfoData) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string[]>;
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'E-commerce',
  'Manufacturing',
  'Education',
  'Real Estate',
  'Legal',
  'Marketing',
  'Consulting',
  'Non-profit',
  'Government',
  'Media & Entertainment',
  'Transportation',
  'Energy',
  'Agriculture',
  'Automotive',
  'Telecommunications',
  'Hospitality',
  'Other'
];

const COMPANY_SIZES = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '500+', label: '500+ employees' }
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SE', label: 'Sweden' },
  { value: 'SG', label: 'Singapore' },
  { value: 'JP', label: 'Japan' },
  { value: 'Other', label: 'Other' }
];

export const CompanyInformationStep: React.FC<CompanyInformationStepProps> = ({
  data,
  onChange,
  onValidationChange,
  errors = {}
}) => {
  const [localData, setLocalData] = useState<CompanyInfoData>({
    ...data,
    country: data.country || 'US' // Set default country
  });
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Initialize and validate form
  useEffect(() => {
    // Initialize with defaults if needed
    if (!localData.country) {
      setLocalData(prev => ({ ...prev, country: 'US' }));
      return; // Exit early to prevent validation with incomplete data
    }

    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!localData.company_name?.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!localData.industry) {
      newErrors.industry = 'Industry is required';
    }

    if (!localData.company_size) {
      newErrors.company_size = 'Company size is required';
    }

    // Website URL validation
    if (localData.website && localData.website.trim()) {
      const urlPattern = /^https?:\/\/.+\..+/;
      if (!urlPattern.test(localData.website)) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    // Phone validation (basic)
    if (localData.phone && localData.phone.trim()) {
      const phonePattern = /^[\+]?[\s\d\-\(\)]{10,}$/;
      if (!phonePattern.test(localData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Only update if errors changed
    const errorsChanged = JSON.stringify(newErrors) !== JSON.stringify(localErrors);
    if (errorsChanged) {
      setLocalErrors(newErrors);
    }
    
    // Check if form is valid
    const isValid = Object.keys(newErrors).length === 0 && 
                   !!localData.company_name?.trim() &&
                   !!localData.industry &&
                   !!localData.company_size;
    
    console.log('Company step validation:', {
      localData,
      errors: newErrors,
      isValid,
      hasCompanyName: !!localData.company_name?.trim(),
      hasIndustry: !!localData.industry,
      hasCompanySize: !!localData.company_size
    });
    
    // Notify parent of changes
    onChange(localData);
    onValidationChange(isValid);
  }, [localData, localErrors, onChange, onValidationChange]);

  // Run initial validation on mount
  useEffect(() => {
    // Trigger validation on initial mount
    const timer = setTimeout(() => {
      console.log('Running initial validation check');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (field: keyof CompanyInfoData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLocalData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: keyof CompanyInfoData) => (
    event: any
  ) => {
    setLocalData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getError = (field: string): string => {
    return errors[field]?.[0] || localErrors[field] || '';
  };

  const hasError = (field: string): boolean => {
    return !!(errors[field]?.length || localErrors[field]);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Company Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tell us about your company so we can better understand your needs and provide tailored solutions.
      </Typography>

      <Grid container spacing={3}>
        {/* Company Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Company Name"
            value={localData.company_name || ''}
            onChange={handleInputChange('company_name')}
            error={hasError('company_name')}
            helperText={getError('company_name')}
            required
            inputProps={{
              'aria-label': 'Company Name',
              'data-testid': 'company-name-input'
            }}
            sx={{ mb: 2 }}
          />
        </Grid>

        {/* Website */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Website"
            value={localData.website || ''}
            onChange={handleInputChange('website')}
            error={hasError('website')}
            helperText={getError('website') || 'https://yourcompany.com'}
            placeholder="https://yourcompany.com"
            inputProps={{
              'aria-label': 'Company Website',
              'data-testid': 'website-input'
            }}
            sx={{ mb: 2 }}
          />
        </Grid>

        {/* Industry */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={INDUSTRIES}
            value={localData.industry || ''}
            onChange={(_, newValue) => {
              setLocalData(prev => ({ ...prev, industry: newValue || '' }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Industry"
                required
                error={hasError('industry')}
                helperText={getError('industry')}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Industry',
                  'data-testid': 'industry-input'
                }}
              />
            )}
            sx={{ mb: 2 }}
          />
        </Grid>

        {/* Company Size */}
        <Grid item xs={12} md={6}>
          <FormControl 
            fullWidth 
            required 
            error={hasError('company_size')}
            sx={{ mb: 2 }}
          >
            <InputLabel>Company Size</InputLabel>
            <Select
              value={localData.company_size || ''}
              onChange={handleSelectChange('company_size')}
              label="Company Size"
              inputProps={{
                'aria-label': 'Company Size',
                'data-testid': 'company-size-select'
              }}
            >
              {COMPANY_SIZES.map((size) => (
                <MenuItem key={size.value} value={size.value}>
                  {size.label}
                </MenuItem>
              ))}
            </Select>
            {hasError('company_size') && (
              <FormHelperText>{getError('company_size')}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Phone */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={localData.phone || ''}
            onChange={handleInputChange('phone')}
            error={hasError('phone')}
            helperText={getError('phone')}
            placeholder="+1 (555) 123-4567"
            inputProps={{
              'aria-label': 'Phone Number',
              'data-testid': 'phone-input'
            }}
            sx={{ mb: 2 }}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={localData.country || 'US'}
              onChange={handleSelectChange('country')}
              label="Country"
              inputProps={{
                'aria-label': 'Country',
                'data-testid': 'country-select'
              }}
            >
              {COUNTRIES.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={localData.address || ''}
            onChange={handleInputChange('address')}
            error={hasError('address')}
            helperText={getError('address')}
            multiline
            rows={2}
            placeholder="123 Business St, City, State/Province, Postal Code"
            inputProps={{
              'aria-label': 'Company Address',
              'data-testid': 'address-input'
            }}
            sx={{ mb: 2 }}
          />
        </Grid>

        {/* Company Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company Description"
            value={localData.company_description || ''}
            onChange={handleInputChange('company_description')}
            error={hasError('company_description')}
            helperText={getError('company_description') || 'Brief description of your company and what you do (optional)'}
            multiline
            rows={3}
            inputProps={{
              'aria-label': 'Company Description',
              'data-testid': 'description-input'
            }}
            sx={{ mb: 2 }}
          />
        </Grid>
      </Grid>

      {/* Summary of required fields */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Required fields:</strong> Company name, industry, and company size are required to continue.
        </Typography>
      </Box>
    </Box>
  );
};